import React from 'react';
import { ListGroup, ListGroupItem } from 'reactstrap';

export interface SortableVerticalListProps {
  /** Stable unique id per row (string). */
  itemIds: string[];
  onReorder: (nextIds: string[]) => void;
  /** Row body (label, meta, etc.) — drag handle is prepended automatically. */
  renderRow: (id: string) => React.ReactNode;
  listGroupClassName?: string;
  itemClassName?: string;
}

/**
 * Vertical drag-and-drop list using native HTML5 DnD.
 * More stable in Bootstrap/Reactstrap modals than pointer sensors.
 */
export const SortableVerticalList: React.FC<SortableVerticalListProps> = ({
  itemIds,
  onReorder,
  renderRow,
  listGroupClassName,
  itemClassName,
}) => {
  const [dragId, setDragId] = React.useState<string | null>(null);

  const move = (from: number, to: number) => {
    if (from < 0 || to < 0 || from === to) return;
    const next = [...itemIds];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onReorder(next);
  };

  return (
    <ListGroup className={listGroupClassName}>
      {itemIds.map((id) => (
        <ListGroupItem
          key={id}
          draggable
          onDragStart={(e) => {
            setDragId(id);
            e.dataTransfer.effectAllowed = 'move';
            e.dataTransfer.setData('text/plain', id);
          }}
          onDragOver={(e) => {
            e.preventDefault();
            e.dataTransfer.dropEffect = 'move';
          }}
          onDrop={(e) => {
            e.preventDefault();
            const droppedId = e.dataTransfer.getData('text/plain') || dragId;
            if (!droppedId || droppedId === id) return;
            const from = itemIds.indexOf(droppedId);
            const to = itemIds.indexOf(id);
            move(from, to);
          }}
          onDragEnd={() => setDragId(null)}
          className={['d-flex align-items-center gap-2 py-3', itemClassName].filter(Boolean).join(' ')}
          style={{ cursor: 'grab' }}
        >
          <span className="text-muted fs-5 flex-shrink-0" aria-label="Drag to reorder">
            <i className="ri-draggable" aria-hidden />
          </span>
          {renderRow(id)}
        </ListGroupItem>
      ))}
    </ListGroup>
  );
};

export default SortableVerticalList;
